import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Sheet,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import { Link, useParams } from 'react-router-dom';
import { documentDisplayRef } from '../../docs/utils/invoiceLedger';
import { CIS_BAND_LABELS, type DirectoryAccount } from '../accounts.model';
import { accountsService } from '../accounts.service';
import {
  ARRANGEMENT_TYPE_LABELS,
  VAT_TREATMENT_LABELS,
  arrangementInvoiceCoverage,
  arrangementInvoiceLabel,
  arrangementLedger,
  arrangementRecurringStatus,
  nextBillingPeriod,
  chargesVat,
  formatGbp,
  netFromGross,
  type BillingArrangement,
} from '../arrangements.model';
import { arrangementsService } from '../arrangements.service';
import { ArrangementFormModal, IssueInvoiceCard, ledgerInvoices } from '../components/ArrangementForms';
import { BILLING_FREQUENCY_LABELS, periodLabel } from '../recurrence.model';
import { ACCOUNT_INVOICE_STATUS_LABELS, normaliseInvoiceStatus } from '../accountInvoice.model';
import { InvoiceActionsMenu, accountInvoicePath } from '../components/InvoiceActionsMenu';

function percent(ratio: number): string {
  return `${Math.round(ratio * 1000) / 10}%`;
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack spacing={0.25}>
      <Typography level="body-xs" textColor="neutral.500" textTransform="uppercase" letterSpacing="0.04em">{label}</Typography>
      <Typography level="body-sm" component="div">{children}</Typography>
    </Stack>
  );
}

/** One arrangement: what it is, who it bills, and the invoices raised against it. */
const ArrangementPage: React.FC = () => {
  const { ref = '' } = useParams<{ ref: string }>();
  const [arrangement, setArrangement] = useState<BillingArrangement | null>(null);
  const [account, setAccount] = useState<DirectoryAccount | null>(null);
  const [accounts, setAccounts] = useState<DirectoryAccount[]>([]);
  const [invoices, setInvoices] = useState<Array<Record<string, any>>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<{ id: string; ref: string } | null>(null);

  const loadInvoices = useCallback(async (row: BillingArrangement) => {
    const grouped = await arrangementsService.invoicesByArrangement([row]);
    // Newest first
    setInvoices((grouped[row.id] || []).sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))));
  }, []);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const row = await arrangementsService.getByRef(ref);
      if (!row) {
        setArrangement(null);
        setError(`Arrangement ${ref} was not found.`);
        return;
      }
      const accountRows = await accountsService.list();
      setArrangement(row);
      setAccounts(accountRows);
      setAccount(accountRows.find((candidate) => candidate.id === row.accountId) || null);
      await loadInvoices(row);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the arrangement');
    } finally {
      setLoading(false);
    }
  }, [ref, loadInvoices]);

  useEffect(() => {
    void load();
  }, [load]);

  const ledger = useMemo(
    () => (arrangement ? arrangementLedger(arrangement, ledgerInvoices(invoices)) : null),
    [arrangement, invoices]
  );

  const toggleStatus = async () => {
    if (!arrangement) return;
    try {
      setArrangement(await arrangementsService.setStatus(arrangement.id, arrangement.status === 'open' ? 'closed' : 'open'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update the arrangement');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1080 }}>
      <Stack spacing={2.5}>
        <Box>
          <Button component={Link} to="/invoicing" size="sm" variant="plain" color="neutral" sx={{ ml: -1 }}>
            ← Invoicing
          </Button>
        </Box>

        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
        {error && <Alert color="danger">{error}</Alert>}

        {!loading && arrangement && ledger && (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
              <Stack spacing={0.5}>
                <Typography level="body-sm" textColor="neutral.500" fontWeight="lg">Arrangement {arrangement.lifecycleRef}</Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography level="h3">{arrangement.title}</Typography>
                  <Chip size="sm" variant="soft">{ARRANGEMENT_TYPE_LABELS[arrangement.arrangementType]}</Chip>
                  <Chip size="sm" variant="soft" color={arrangement.status === 'open' ? 'primary' : 'neutral'}>
                    {arrangement.status === 'open' ? 'Open' : 'Closed'}
                  </Chip>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Button variant="outlined" color="neutral" onClick={() => setEditing(true)}>Edit</Button>
                <Button variant="outlined" color="neutral" onClick={toggleStatus}>
                  {arrangement.status === 'open' ? 'Close' : 'Reopen'}
                </Button>
              </Stack>
            </Stack>

            <Card variant="outlined">
              <Grid container spacing={2}>
                <Grid xs={12} sm={6} md={3}>
                  <Fact label="Account">
                    {account?.companyName || 'Unknown account'}
                    {account && (account.billingEmail || account.email) && (
                      <Typography level="body-xs" textColor="neutral.500" display="block">{account.billingEmail || account.email}</Typography>
                    )}
                  </Fact>
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <Fact label="VAT and CIS">
                    {VAT_TREATMENT_LABELS[arrangement.vatTreatment]}
                    {chargesVat(arrangement.vatTreatment) ? ` at ${percent(arrangement.vatPct)}` : ''}
                    <Typography level="body-xs" textColor="neutral.500" display="block">
                      {arrangement.cisBand === 'none' ? 'No CIS' : `CIS: ${CIS_BAND_LABELS[arrangement.cisBand]}, deducted by the customer`}
                    </Typography>
                  </Fact>
                </Grid>
                <Grid xs={6} md={3}>
                  <Fact label="Payment terms">{arrangement.paymentTermsInDays} days</Fact>
                </Grid>
                <Grid xs={6} md={3}>
                  <Fact label="Deposit">{arrangement.depositPercentage != null ? percent(arrangement.depositPercentage) : 'None'}</Fact>
                </Grid>
              </Grid>
              {arrangement.description && (
                <>
                  <Divider />
                  <Fact label="Description (printed on invoices)">{arrangement.description}</Fact>
                </>
              )}
              <Divider />
              {arrangement.frequency !== 'none' && (() => {
                const next = nextBillingPeriod(arrangement);
                const status = arrangementRecurringStatus(arrangement);
                return (
                  <>
                    <Grid container spacing={2}>
                      <Grid xs={12} sm={4}>
                        <Fact label="Billing">
                          {BILLING_FREQUENCY_LABELS[arrangement.frequency]} · {formatGbp(arrangement.recurringNet)} ex VAT
                        </Fact>
                      </Grid>
                      <Grid xs={12} sm={8}>
                        <Fact label="Next invoice">
                          {next ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <span>{periodLabel(next)}</span>
                              {status === 'due' && <Chip size="sm" color="warning" variant="soft">Due</Chip>}
                            </Stack>
                          ) : `None. Ended ${arrangement.endsOn || ''}`}
                        </Fact>
                      </Grid>
                    </Grid>
                    <Divider />
                  </>
                );
              })()}
              {ledger.agreedGross != null ? (
                <Stack spacing={0.75}>
                  <Grid container spacing={2}>
                    <Grid xs={4}>
                      <Fact label="Agreed">
                        {formatGbp(ledger.agreedGross)}
                        {chargesVat(arrangement.vatTreatment) && (
                          <Typography level="body-xs" textColor="neutral.500" display="block">
                            {formatGbp(netFromGross(ledger.agreedGross, arrangement.vatTreatment, arrangement.vatPct))} ex VAT
                          </Typography>
                        )}
                      </Fact>
                    </Grid>
                    <Grid xs={4}><Fact label="Invoiced">{formatGbp(ledger.invoicedGross)}</Fact></Grid>
                    <Grid xs={4}><Fact label="Left to invoice">{formatGbp(ledger.remainingGross)}</Fact></Grid>
                  </Grid>
                  <LinearProgress determinate value={Math.min(100, (ledger.invoicedGross / ledger.agreedGross) * 100)} />
                </Stack>
              ) : (
                <Fact label="Invoiced so far">
                  {formatGbp(ledger.invoicedGross)}
                  <Typography level="body-xs" textColor="neutral.500" display="block">Open-ended: no agreed total.</Typography>
                </Fact>
              )}
            </Card>

            {notice && (
              <Alert
                color="success"
                endDecorator={<Button size="sm" variant="soft" color="success" component={Link} to={accountInvoicePath(notice.id)}>Open invoice</Button>}
              >
                Draft invoice {notice.ref} created.
              </Alert>
            )}

            <Stack spacing={1}>
              <Typography level="title-lg">Invoices</Typography>
              <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                <Table hoverRow>
                  <thead>
                    <tr>
                      <th style={{ width: 130 }}>Invoice</th>
                      <th style={{ width: 100 }}>Type</th>
                      <th>Covers</th>
                      <th style={{ width: 105 }}>Date</th>
                      <th style={{ width: 80 }}>Status</th>
                      <th style={{ width: 110, textAlign: 'right' }}>Total</th>
                      <th style={{ width: 48 }} aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => {
                      const invoiceRef = documentDisplayRef({ kind: 'Invoice', invoiceRef: invoice.invoice_ref, lifecycleRef: invoice.lifecycle_ref });
                      const status = normaliseInvoiceStatus(invoice.status);
                      return (
                        <tr key={invoice.id} style={status === 'void' ? { opacity: 0.55 } : undefined}>
                          <td><Link to={accountInvoicePath(invoice.id)}>{invoiceRef}</Link></td>
                          <td>{arrangementInvoiceLabel(invoice.source_survey_event, invoice.invoice_role)}</td>
                          <td>{arrangementInvoiceCoverage(arrangement, invoice.source_survey_event, invoice.invoice_role)}</td>
                          <td>{String(invoice.issued_at || invoice.created_at || '').slice(0, 10)}</td>
                          <td><Chip size="sm" variant="soft">{ACCOUNT_INVOICE_STATUS_LABELS[status]}</Chip></td>
                          <td style={{ textAlign: 'right' }}>{formatGbp(Number(invoice.gross_total ?? 0))}</td>
                          <td>
                            <InvoiceActionsMenu
                              invoice={{
                                id: invoice.id,
                                status: invoice.status,
                                invoiceRef,
                                freeagentInvoiceUrl: invoice.freeagent_invoice_url,
                              }}
                              onChanged={() => void load(true)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                    {invoices.length === 0 && (
                      <tr>
                        <td colSpan={7}>
                          <Typography level="body-sm" textColor="neutral.500" sx={{ py: 2, textAlign: 'center' }}>
                            No invoices yet. Raise the first one below.
                          </Typography>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Sheet>
            </Stack>

            <IssueInvoiceCard
              arrangement={arrangement}
              invoices={invoices}
              onIssued={(invoice) => {
                setNotice({ id: invoice.id, ref: documentDisplayRef(invoice) });
                // Reload the arrangement as well: recurring invoices move it on to the next period
                void load(true);
              }}
            />
          </>
        )}
      </Stack>

      {arrangement && (
        <ArrangementFormModal
          open={editing}
          arrangement={arrangement}
          invoices={invoices}
          accounts={accounts}
          onAccountCreated={(created) => setAccounts((current) => [...current, created])}
          onClose={() => setEditing(false)}
          onSaved={(updated) => {
            setEditing(false);
            setArrangement(updated);
            setAccount(accounts.find((candidate) => candidate.id === updated.accountId) || null);
          }}
        />
      )}
    </Box>
  );
};

export default ArrangementPage;
